import { announceGlobalLiveRegion } from '@theme/live-region';

/**
 * <s-lift-configurator> — switches between lift models and their options in place.
 * Reads per-model variant JSON from [data-config-json], keeps input[name="id"] on the
 * product form in sync with the chosen variant, and updates stage image, price,
 * breakdown, availability and URL without reloading. Add to cart is handled by
 * <s-product-form>.
 */
class SLiftConfigurator extends HTMLElement {
  #ac;
  #models = new Map();
  #selections = new Map();
  #model;
  #variant;
  #money;
  #stageSrc = '';
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    let data = [];
    try {
      data = JSON.parse(this.querySelector('[data-config-json]')?.textContent || '[]');
    } catch {
      return;
    }
    for (const model of data) {
      for (const variant of model.variants) {
        if (variant.image) {
          const thumb = new URL(variant.image.src, window.location.origin);
          thumb.searchParams.set('width', '160');
          variant.image.thumb = thumb.href;
        }
      }
      this.#models.set(model.handle, model);
    }
    if (!this.#models.size) return;

    try {
      this.#money = new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      });
    } catch {
      this.#money = null;
    }

    const initialHandle = this.#models.has(this.dataset.initialModel)
      ? this.dataset.initialModel
      : this.#models.keys().next().value;
    const initialId = Number(this.dataset.initialVariant);

    for (const model of this.#models.values()) {
      const start =
        (model.handle === initialHandle && model.variants.find((v) => v.id === initialId)) ||
        model.variants.find((v) => v.available) ||
        model.variants[0];
      this.#selections.set(model.handle, [...start.options]);
    }

    this.addEventListener('change', (event) => this.#onChange(event), opts);
    this.addEventListener('click', (event) => this.#onThumb(event), opts);

    this.#setModel(initialHandle, { initial: true });
    this.#watchStickyBar();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.matches('[data-model-input]')) {
      this.#setModel(input.value);
      return;
    }

    if (input.matches('[data-option-input]')) {
      const group = input.closest('[data-option-group]');
      const index = Number(group?.dataset.optionIndex);
      const selection = this.#selections.get(this.#model.handle);
      selection[index] = input.value;
      this.#resolve(index);
      this.#render();
    }
  }

  #setModel(handle, { initial = false } = {}) {
    const model = this.#models.get(handle);
    if (!model) return;
    this.#model = model;
    const productForm = this.querySelector('[data-product-form]');
    if (productForm && model.id) productForm.dataset.productId = String(model.id);

    for (const panel of this.querySelectorAll('[data-model-panel]')) {
      panel.hidden = panel.dataset.modelPanel !== handle;
    }
    // Model-specific content anywhere on the page (specs, summaries, galleries).
    for (const el of document.querySelectorAll('[data-config-model]')) {
      el.hidden = el.dataset.configModel !== handle;
    }
    for (const input of this.querySelectorAll('[data-model-input]')) {
      input.checked = input.value === handle;
    }

    const title = this.querySelector('[data-model-title]');
    if (title) title.textContent = model.title;

    this.#resolve(-1);
    this.#render({ initial });
  }

  /** Make the selection point at an available variant, keeping the option the customer just changed. */
  #resolve(changedIndex) {
    const selection = this.#selections.get(this.#model.handle);
    const exact = this.#find(selection);
    if (exact?.available) return;

    const fallback =
      this.#model.variants.find(
        (v) => v.available && (changedIndex < 0 || v.options[changedIndex] === selection[changedIndex])
      ) ||
      exact ||
      this.#model.variants.find((v) => v.available) ||
      this.#model.variants[0];

    this.#selections.set(this.#model.handle, [...fallback.options]);
  }

  #find(selection) {
    return this.#model.variants.find((v) => v.options.every((value, i) => value === selection[i]));
  }

  #render({ initial = false } = {}) {
    const model = this.#model;
    const selection = this.#selections.get(model.handle);
    const variant = this.#find(selection) || model.variants[0];
    const changedVariant = this.#variant?.id !== variant.id;
    this.#variant = variant;

    const panel = this.querySelector(`[data-model-panel="${CSS.escape(model.handle)}"]`);

    // Options: checked state, unavailable combinations, price differences.
    for (const group of panel?.querySelectorAll('[data-option-group]') ?? []) {
      const index = Number(group.dataset.optionIndex);
      const selected = group.querySelector('[data-selected-value]');
      if (selected) selected.textContent = selection[index];

      for (const input of group.querySelectorAll('[data-option-input]')) {
        const candidate = [...selection];
        candidate[index] = input.value;
        const match = this.#find(candidate);
        input.checked = input.value === selection[index];
        // Styles remain selectable if another crossbeam is available; resolve keeps that style.
        const available = index === 0
          ? model.variants.some((v) => v.available && v.options[index] === input.value)
          : Boolean(match?.available);
        input.disabled = !available;

        const delta = input.parentElement?.querySelector('[data-delta]');
        if (delta) {
          const base = [...selection];
          base[index] = this.#firstValue(index);
          const baseVariant = this.#find(base);
          const diff = match && baseVariant ? match.price - baseVariant.price : 0;
          delta.textContent = diff > 0 ? `+${this.#format(diff)}` : '';
        }
      }
    }

    // Form + URL.
    const idInput = this.querySelector('[data-config-variant-input]');
    if (idInput) idInput.value = String(variant.id);
    if (!initial) {
      const url = new URL(model.url, window.location.origin);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url);
    }

    // Copy.
    const label = `${model.title} · ${variant.title}`;
    for (const el of this.querySelectorAll('[data-review-title], [data-stage-caption], [data-sticky-title]')) {
      el.textContent = label;
    }

    // Price.
    for (const el of this.querySelectorAll('[data-price], [data-sticky-price]')) {
      el.innerHTML = '';
      const bdi = document.createElement('bdi');
      bdi.textContent = variant.priceFormatted;
      el.append(bdi);
    }
    const compare = this.querySelector('[data-compare-price]');
    if (compare) {
      const show = variant.compare > variant.price;
      compare.hidden = !show;
      compare.innerHTML = '';
      if (show) {
        const bdi = document.createElement('bdi');
        bdi.textContent = variant.compareFormatted;
        compare.append(bdi);
      }
    }
    this.#renderBreakdown(selection, variant);

    // Add to cart.
    const labelText = variant.available ? this.dataset.labelAdd : this.dataset.labelSoldOut;
    for (const button of this.querySelectorAll('[data-add-button]')) {
      button.disabled = !variant.available;
      const span = button.querySelector('[data-add-label]');
      if (span && labelText) span.textContent = labelText;
    }

    // Image.
    const image = this.#styleImage(variant);
    if (image) this.#showImage(image, { animate: !initial });
    this.#syncThumbs(image);

    if (!initial && changedVariant) {
      announceGlobalLiveRegion(`${label}, ${variant.priceFormatted}`);
    }
  }

  #renderBreakdown(selection, variant) {
    const list = this.querySelector('[data-breakdown]');
    if (!list) return;
    list.replaceChildren();

    const groups = this.querySelectorAll(`[data-model-panel="${CSS.escape(this.#model.handle)}"] [data-option-group]`);
    for (const group of groups) {
      const index = Number(group.dataset.optionIndex);
      const base = [...selection];
      base[index] = this.#firstValue(index);
      const baseVariant = this.#find(base);
      if (!baseVariant) continue;
      const diff = variant.price - baseVariant.price;
      if (diff <= 0) continue;

      const item = document.createElement('li');
      item.className = 'flex justify-between gap-4';
      const name = document.createElement('span');
      name.textContent = selection[index];
      const value = document.createElement('span');
      value.textContent = `+${this.#format(diff)}`;
      item.append(name, value);
      list.append(item);
    }
  }

  #firstValue(index) {
    return this.#model.variants[0]?.options[index];
  }

  /** Exact variant image, else the image of the same first-option value, else the model image. */
  #styleImage(variant) {
    if (variant.image) return variant.image;
    const sibling = this.#model.variants.find((v) => v.image && v.options[0] === variant.options[0]);
    return sibling?.image || this.#model.fallbackImage;
  }

  #showImage(image, { animate = true } = {}) {
    const img = this.querySelector('[data-stage-image]');
    if (!img || !image || this.#stageSrc === image.src) return;
    this.#stageSrc = image.src;

    const apply = () => {
      img.srcset = image.srcset || '';
      img.src = image.src;
      img.width = image.width;
      img.height = image.height;
      img.alt = image.alt || '';
    };

    if (!animate || this.#reducedMotion.matches || !img.animate) {
      apply();
      return;
    }

    const preload = new Image();
    preload.sizes = img.sizes;
    preload.srcset = image.srcset || '';
    preload.src = image.src;
    const swap = () => {
      if (this.#stageSrc !== image.src) return;
      const out = img.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, easing: 'ease-out', fill: 'forwards' });
      out.onfinish = () => {
        if (this.#stageSrc !== image.src) {
          out.cancel();
          return;
        }
        apply();
        out.cancel();
        img.animate(
          [
            { opacity: 0, transform: 'scale(0.985)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          { duration: 420, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
        );
      };
    };
    preload.decode ? preload.decode().then(swap, swap) : swap();
  }

  #onThumb(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-thumb]') : null;
    if (!button) return;

    if (button.dataset.thumb === 'style') {
      this.#showImage(this.#styleImage(this.#variant));
    } else {
      this.#showImage({
        src: button.dataset.src,
        srcset: button.dataset.srcset,
        width: Number(button.dataset.width),
        height: Number(button.dataset.height),
        alt: button.dataset.alt,
      });
    }
    const list = button.closest('ul');
    for (const thumb of list?.querySelectorAll('[data-thumb]') ?? []) {
      const active = thumb === button;
      thumb.setAttribute('aria-pressed', String(active));
      thumb.classList.toggle('ring-1', active);
      thumb.classList.toggle('ring-0', !active);
    }
  }

  #syncThumbs(styleImage) {
    for (const list of document.querySelectorAll('[data-config-model]')) {
      const styleThumb = list.querySelector('[data-thumb="style"]');
      if (!styleThumb) continue;
      if (list.dataset.configModel === this.#model.handle) {
        const img = styleThumb.querySelector('[data-thumb-style-image]');
        if (img && styleImage) img.src = styleImage.thumb || styleImage.src;
      }
      for (const thumb of list.querySelectorAll('[data-thumb]')) {
        const active = thumb === styleThumb;
        thumb.setAttribute('aria-pressed', String(active));
        thumb.classList.toggle('ring-1', active);
        thumb.classList.toggle('ring-0', !active);
      }
    }
  }

  #format(cents) {
    const amount = cents / 100;
    return this.#money ? this.#money.format(amount) : amount.toFixed(2);
  }

  /** Show the mobile purchase bar only while the main add-to-cart button is off screen. */
  #watchStickyBar() {
    const bar = this.querySelector('[data-sticky-bar]');
    const main = this.querySelector('[data-main-add]');
    if (!bar || !main || !('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting;
      bar.toggleAttribute('data-visible', show);
      bar.inert = !show;
    });
    this.#observer.observe(main);
  }
}

if (!customElements.get('s-lift-configurator')) {
  customElements.define('s-lift-configurator', SLiftConfigurator);
}
